import { AnyAction } from 'redux';
import { AppThunk } from '../index';
import { type CancelablePromise } from '@upsales/components/Utils/CancelablePromise';
import { ExternalIntegration } from '../../../components/hooks/integrations/externalIntegrationTypes';

export const SET_AVAILABLE_INTEGRATIONS = '[ExternalIntegration] SetAvailableIntegrations';
export const SET_ALL_INTEGRATIONS = '[ExternalIntegration] SetAllIntegrations';
export const SET_CONNECTED_INTEGRATIONS = '[ExternalIntegration] SetConnectedIntegrations';
export const SET_TOKENS = '[ExternalIntegration] setTokens';
export const SET_CACHE_DATA = '[ExternalIntegration] SetCacheData';
export const SET_CACHE_PROMISE = '[ExternalIntegration] SetCachePromise';
export const CLEAR_CACHE = '[ExternalIntegration] ClearCache';
export const CLEAR_CACHE_PROMISE = '[ExternalIntegration] ClearCachePromise';
export const SET_INITIALISED = '[ExternalIntegration] SetInitialised';

const CACHE_TTL = 30 * 1000;

interface CacheData<T> {
	data: T | null;
	timestamp: number;
	promise: CancelablePromise<T> | null;
}

type ExternalIntegrationState = {
	availableIntegrations: ExternalIntegration[];
	connectedIntegrations: ExternalIntegration[];
	allIntegrations: ExternalIntegration[];
	tokens: {
		token: string;
		upsalesToken: string;
		projectId: string;
	};
	cache: Map<string, CacheData<any>>;
	initialised: boolean;
};

export const initialState: ExternalIntegrationState = {
	availableIntegrations: [],
	connectedIntegrations: [],
	allIntegrations: [],
	tokens: {
		token: '',
		upsalesToken: '',
		projectId: ''
	},
	cache: new Map(),
	initialised: false
};

const ACTION_HANDLERS: { [key: string]: (s: ExternalIntegrationState, a: AnyAction) => ExternalIntegrationState } = {
	[SET_AVAILABLE_INTEGRATIONS]: (state, { availableIntegrations }) => ({ ...state, availableIntegrations }),
	[SET_CONNECTED_INTEGRATIONS]: (state, { connectedIntegrations }) => ({ ...state, connectedIntegrations }),
	[SET_ALL_INTEGRATIONS]: (state, { allIntegrations }) => ({ ...state, allIntegrations }),
	[SET_TOKENS]: (state, { tokens }) => ({ ...state, tokens }),
	[SET_INITIALISED]: (state, { initialised }) => ({ ...state, initialised }),
	[SET_CACHE_DATA]: (state, { cacheKey, data }) => {
		const newCache = new Map(state.cache);
		const existing = newCache.get(cacheKey) || { data: null, timestamp: 0, promise: null };
		newCache.set(cacheKey, { ...existing, data, timestamp: Date.now() });
		return { ...state, cache: newCache };
	},
	[SET_CACHE_PROMISE]: (state, { cacheKey, promise }) => {
		const newCache = new Map(state.cache);
		const existing = newCache.get(cacheKey) || { data: null, timestamp: 0, promise: null };
		newCache.set(cacheKey, { ...existing, promise });
		return { ...state, cache: newCache };
	},
	[CLEAR_CACHE]: (state, { cacheKey }) => {
		const newCache = new Map(state.cache);
		const existing = newCache.get(cacheKey);
		if (existing?.promise) {
			existing.promise.cancel();
		}
		newCache.set(cacheKey, { data: null, timestamp: 0, promise: null });
		return { ...state, cache: newCache };
	},
	[CLEAR_CACHE_PROMISE]: (state, { cacheKey }) => {
		const newCache = new Map(state.cache);
		const existing = newCache.get(cacheKey);
		if (existing?.promise) {
			existing.promise.cancel();
		}
		if (existing) {
			newCache.set(cacheKey, { ...existing, promise: null });
		}
		return { ...state, cache: newCache };
	}
};

export const setIntegrations =
	(
		integrations: {
			availableIntegrations: ExternalIntegration[];
			connectedIntegrations: ExternalIntegration[];
		} | null
	): AppThunk =>
	dispatch => {
		if (!integrations) {
			dispatch({
				type: SET_INITIALISED,
				initialised: true
			});
			return;
		}
		const availableIntegrations = integrations?.availableIntegrations ?? [];
		const connectedIntegrations = integrations?.connectedIntegrations ?? [];
		const allIntegrations = [...availableIntegrations, ...connectedIntegrations];
		dispatch({
			type: SET_AVAILABLE_INTEGRATIONS,
			availableIntegrations
		});
		dispatch({
			type: SET_CONNECTED_INTEGRATIONS,
			connectedIntegrations
		});
		dispatch({
			type: SET_ALL_INTEGRATIONS,
			allIntegrations
		});
		dispatch({
			type: SET_INITIALISED,
			initialised: true
		});
	};

export const setTokens = (tokens: { token: string; upsalesToken: string; projectId: string }) => ({
	type: SET_TOKENS,
	tokens
});

export const setInitialised = (initialised: boolean) => ({
	type: SET_INITIALISED,
	initialised
});

export const setCacheData = <T>(cacheKey: string, data: T) => ({
	type: SET_CACHE_DATA,
	cacheKey,
	data
});

export const setCachePromise = <T>(cacheKey: string, promise: CancelablePromise<T>) => ({
	type: SET_CACHE_PROMISE,
	cacheKey,
	promise
});

export const clearCache = (cacheKey: string) => ({
	type: CLEAR_CACHE,
	cacheKey
});

export const clearCachePromise = (cacheKey: string) => ({
	type: CLEAR_CACHE_PROMISE,
	cacheKey
});

export const getCacheData = <T>(state: ExternalIntegrationState, cacheKey: string): T | null => {
	const cacheEntry = state.cache.get(cacheKey);
	return cacheEntry?.data || null;
};

export const getCachePromise = <T>(state: ExternalIntegrationState, cacheKey: string): CancelablePromise<T> | null => {
	const cacheEntry = state.cache.get(cacheKey);
	return cacheEntry?.promise || null;
};

export const isCacheValid = (state: ExternalIntegrationState, cacheKey: string): boolean => {
	const cacheEntry = state.cache.get(cacheKey);
	return cacheEntry?.data !== null && Date.now() - (cacheEntry?.timestamp || 0) < CACHE_TTL;
};

export const FETCH_PARAGON_TOKENS = '[ExternalIntegration] FetchParagonTokens';

export const fetchParagonTokens = (): AppThunk => async (dispatch, getState) => {
	const state = getState().ExternalIntegration;
	const cacheKey = 'tokens';

	// Check if we already have valid tokens
	if (state.tokens?.token && state.tokens?.upsalesToken && state.tokens?.projectId) {
		return;
	}

	// Check if cache is valid
	if (isCacheValid(state, cacheKey)) {
		const cachedData = getCacheData<{
			paragonToken: string;
			upsalesToken: string;
			projectId: string;
		}>(state, cacheKey);
		if (cachedData) {
			const mappedData = {
				token: cachedData.paragonToken,
				upsalesToken: cachedData.upsalesToken,
				projectId: cachedData.projectId
			};
			dispatch(setTokens(mappedData));
			return;
		}
	}

	// Check if there's already a pending request
	if (getCachePromise(state, cacheKey)) {
		return;
	}

	try {
		const { makeCancelable } = await import('@upsales/components/Utils/CancelablePromise');
		const ExternalIntegration = (await import('App/resources/ExternalIntegration')).default;

		const cancellable = makeCancelable(ExternalIntegration.token('paragon'));
		dispatch(setCachePromise(cacheKey, cancellable));

		const response = await cancellable.promise;
		const apiTokenData = {
			paragonToken: response.paragonToken,
			upsalesToken: response.upsalesToken,
			projectId: response.projectId || ''
		};

		// Update cache with API format and Redux with mapped format
		dispatch(setCacheData(cacheKey, apiTokenData));

		const reduxTokenData = {
			token: response.paragonToken,
			upsalesToken: response.upsalesToken,
			projectId: response.projectId || ''
		};
		dispatch(setTokens(reduxTokenData));
		dispatch(clearCachePromise(cacheKey));
	} catch (error: unknown) {
		const logErrorModule = await import('App/babel/helpers/logError');
		logErrorModule.default(error, 'Error fetching Paragon tokens');
		dispatch(clearCachePromise(cacheKey));
	}
};

export default (state = { ...initialState }, action: AnyAction): ExternalIntegrationState => {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
};
